import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import { type Lead, type LeadNote, fmtDateTime } from "../types";

interface Props {
  lead: Lead;
  tenantId: string;
  onNoteCountChange?: (count: number) => void;
}

const MAX_LENGTH = 1000;

export default function NotesTab({ lead, tenantId, onNoteCountChange }: Props) {
  const [notes, setNotes]             = useState<LeadNote[]>([]);
  const [loading, setLoading]         = useState(true);
  const [draft, setDraft]             = useState("");
  const [saving, setSaving]           = useState(false);
  const [deletingId, setDeletingId]   = useState<string | null>(null);
  const [editingId, setEditingId]     = useState<string | null>(null);
  const [editDraft, setEditDraft]     = useState("");

  /* ── Fetch notes ── */
  const fetchNotes = useCallback(async () => {
    const { data, error } = await supabase
      .from("lead_notes")
      .select("*")
      .eq("lead_id", lead.id)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false });

    if (!error) {
      const list = (data as LeadNote[]) ?? [];
      setNotes(list);
      onNoteCountChange?.(list.length);
    }
    setLoading(false);
  }, [lead.id, tenantId, onNoteCountChange]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  /* ── Add note ── */
  const handleAdd = async () => {
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);

    const { data, error } = await supabase
      .from("lead_notes")
      .insert({
        tenant_id: tenantId,
        lead_id:   lead.id,
        user_id:   (await supabase.auth.getUser()).data.user?.id ?? null,
        note:      text,
      })
      .select("*")
      .maybeSingle();

    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
    } else if (data) {
      const updated = [data as LeadNote, ...notes];
      setNotes(updated);
      setDraft("");
      onNoteCountChange?.(updated.length);
    }
    setSaving(false);
  };

  /* ── Delete note ── */
  const handleDelete = async (id: string) => {
    setDeletingId(id);
    const { error } = await supabase
      .from("lead_notes")
      .delete()
      .eq("id", id)
      .eq("tenant_id", tenantId);

    if (!error) {
      const updated = notes.filter((n) => n.id !== id);
      setNotes(updated);
      onNoteCountChange?.(updated.length);
    }
    setDeletingId(null);
  };

  /* ── Start editing ── */
  const startEdit = (note: LeadNote) => {
    setEditingId(note.id);
    setEditDraft(note.note);
  };

  /* ── Save edit ── */
  const saveEdit = async (id: string) => {
    const text = editDraft.trim();
    if (!text) return;
    const { error } = await supabase
      .from("lead_notes")
      .update({ note: text })
      .eq("id", id)
      .eq("tenant_id", tenantId);

    if (!error) {
      setNotes((prev) => prev.map((n) => n.id === id ? { ...n, note: text } : n));
    }
    setEditingId(null);
    setEditDraft("");
  };

  /* ── Keyboard shortcuts ── */
  const onKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) action();
    if (e.key === "Escape") { setEditingId(null); setEditDraft(""); }
  };

  const charLeft = MAX_LENGTH - draft.length;
  const editCharLeft = MAX_LENGTH - editDraft.length;

  return (
    <div className="space-y-4">

      {/* ── Compose box ── */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-slate-600 text-xs font-medium uppercase tracking-wider">New Note</h3>
          {notes.length > 0 && (
            <span className="text-slate-300 text-xs">{notes.length} note{notes.length !== 1 ? "s" : ""}</span>
          )}
        </div>

        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => onKeyDown(e, handleAdd)}
          rows={3}
          maxLength={MAX_LENGTH}
          placeholder="Add a note — call made, client feedback, follow-up plan, anything relevant..."
          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all resize-none"
        />

        <div className="flex items-center justify-between mt-2.5">
          <span className={`text-[10px] font-mono ${charLeft < 100 ? "text-amber-500" : "text-slate-300"}`}>
            {charLeft} chars left · ⌘Enter to save
          </span>
          <button
            onClick={handleAdd}
            disabled={saving || !draft.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            {saving
              ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <i className="ri-add-line text-sm"></i>
            }
            Add Note
          </button>
        </div>
      </div>

      {/* ── Notes list ── */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : notes.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 bg-white rounded-xl border border-slate-200 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-100">
            <i className="ri-sticky-note-2-line text-slate-300 text-xl"></i>
          </div>
          <p className="text-slate-500 text-sm font-medium">No notes yet</p>
          <p className="text-slate-400 text-xs max-w-xs leading-relaxed">
            Track every interaction — calls, emails, meetings and follow-up plans here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div
              key={note.id}
              className="bg-white rounded-xl border border-slate-200 p-4 group transition-all hover:border-slate-300"
            >
              {editingId === note.id ? (
                /* ── Edit mode ── */
                <div className="space-y-2.5">
                  <textarea
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onKeyDown={(e) => onKeyDown(e, () => saveEdit(note.id))}
                    rows={3}
                    maxLength={MAX_LENGTH}
                    autoFocus
                    className="w-full bg-slate-50 border border-[#C8A96E]/50 rounded-lg px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-[#C8A96E] transition-all resize-none"
                  />
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-mono ${editCharLeft < 100 ? "text-amber-500" : "text-slate-300"}`}>
                      {editCharLeft} chars left
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setEditingId(null); setEditDraft(""); }}
                        className="px-3 py-1.5 text-xs text-slate-400 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer whitespace-nowrap"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => saveEdit(note.id)}
                        disabled={!editDraft.trim()}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-save-line text-xs"></i>
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* ── View mode ── */
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 shrink-0 mt-0.5">
                    <i className="ri-sticky-note-2-line text-[#C8A96E] text-xs"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap break-words">
                      {note.note}
                    </p>
                    <p className="text-slate-300 text-[10px] mt-2 flex items-center gap-1.5">
                      <i className="ri-time-line"></i>
                      {fmtDateTime(note.created_at)}
                    </p>
                  </div>
                  {/* Actions — visible on hover */}
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => startEdit(note)}
                      title="Edit note"
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-[#9a7840] hover:bg-[#C8A96E]/10 transition-all cursor-pointer"
                    >
                      <i className="ri-edit-2-line text-xs"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(note.id)}
                      disabled={deletingId === note.id}
                      title="Delete note"
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-400 hover:bg-red-50 transition-all cursor-pointer"
                    >
                      {deletingId === note.id
                        ? <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" />
                        : <i className="ri-delete-bin-6-line text-xs"></i>
                      }
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}